import React, { useState } from "react";
import "./AboutPage.css";
import { Youtube, Mail, Linkedin, Instagram } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import ApiName from "../../../ApiName";

// -----------------------------------------------------
// Utility: Extract YouTube Embed URL
// -----------------------------------------------------
const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}` : url;
};

// -----------------------------------------------------
// FadeInImage Component (Progressive Loading)
// -----------------------------------------------------
const FadeInImage = ({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="relative w-full h-full overflow-hidden">
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
      )}
      <motion.img
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: isLoaded ? 1 : 0, scale: isLoaded ? 1 : 1.05 }}
        transition={{ duration: 1, ease: "easeOut" }}
        src={src}
        alt={alt}
        className={`${className} transition-all`}
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
      />
    </div>
  );
};

// -----------------------------------------------------
// About Page Component
// -----------------------------------------------------
const AboutPage = () => {
  // Fetch data with React Query
  const { data: aboutData, isLoading, isError } = useQuery({
    queryKey: ["about"],
    queryFn: async () => {
      const response = await axios.get(`${ApiName}/About`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (isError || !aboutData) {
    return (
      <div className="flex justify-center items-center h-[60vh] text-gray-500 sans-light">
        Failed to load about details.
      </div>
    );
  }

  const embedUrl = getYoutubeEmbedUrl(aboutData.aboutVideo);

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
    >
      {/* Hero Section: Split Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mb-20 items-center">
        {/* Left: Image */}
        {aboutData.aboutImage && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full aspect-[4/5] shadow-2xl rounded-sm overflow-hidden bg-gray-100"
          >
            <FadeInImage
              src={`${ApiName}/${aboutData.aboutImage}`}
              className="w-full h-full object-cover"
              alt="About Sareh Abbasi"
            />
          </motion.div>
        )}

        {/* Right: Biography */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-col justify-center"
        >
          <h1 className="text-3xl md:text-4xl sans-bold tracking-widest text-gray-900 mb-8 uppercase">
            About The Artist
          </h1>
          <p className="text-sm md:text-base leading-relaxed text-justify text-gray-600 sans-light">
            {aboutData.aboutDesc}
          </p>
        </motion.div>
      </div>

      {/* Video Section */}
      {embedUrl && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-24"
        >
          <div className="video-container shadow-2xl rounded-sm overflow-hidden mb-6 bg-gray-900">
            <iframe
              width="560"
              height="315"
              src={embedUrl}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="About Video"
            ></iframe>
          </div>
          {aboutData.aboutVideoDesc && (
            <p className="w-full mx-auto text-xs md:text-sm text-center sans-bold text-gray-500 tracking-widest uppercase">
              {aboutData.aboutVideoDesc}
            </p>
          )}
        </motion.div>
      )}

      {/* Lists Section: Education & Exhibitions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-20">
        {/* Education */}
        {aboutData.aboutEducations?.length > 0 && (
          <div>
            <h2 className="mb-8 text-xl sans-bold text-gray-900 tracking-widest uppercase border-b pb-4">
              Education
            </h2>
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{ once: true }}>
              {aboutData.aboutEducations.map((x) => (
                <motion.div key={x.id} variants={itemVariants} className="mb-6 flex gap-4">
                  <div className="w-1 h-full min-h-[2rem] bg-gray-200"></div>
                  <p className="text-sm md:text-base sans-light text-gray-600 leading-relaxed">
                    {x.name}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        )}

        {/* Exhibitions */}
        {aboutData.aboutExhibitions?.length > 0 && (
          <div>
            <h2 className="mb-8 text-xl sans-bold text-gray-900 tracking-widest uppercase border-b pb-4">
              Exhibitions
            </h2>
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{ once: true }}>
              {aboutData.aboutExhibitions.map((x) => (
                <motion.div key={x.id} variants={itemVariants} className="mb-6 flex gap-4">
                  <div className="w-1 h-full min-h-[2rem] bg-gray-200"></div>
                  <p className="text-sm md:text-base sans-light text-gray-600 leading-relaxed">
                    {x.name}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        )}
      </div>

      {/* Social Links Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="flex justify-center w-full pt-12 border-t border-gray-200"
      >
        <ul className="flex items-center gap-10">
          <li>
            <a href="https://www.youtube.com/@sareabbasi" target="_blank" rel="noreferrer" className="block text-gray-500 hover:text-red-600 transition-colors duration-300">
              <Youtube size={28} strokeWidth={1.5} />
            </a>
          </li>
          <li>
            <a href="mailto:sare.abbasi64@gmail.com" className="block text-gray-500 hover:text-gray-900 transition-colors duration-300">
              <Mail size={28} strokeWidth={1.5} />
            </a>
          </li>
          <li>
            <a href="https://linkedin.com/in/sareh-abbasi-1a5579252" target="_blank" rel="noreferrer" className="block text-gray-500 hover:text-blue-600 transition-colors duration-300">
              <Linkedin size={28} strokeWidth={1.5} />
            </a>
          </li>
          <li>
            <a href="https://www.instagram.com/sareabbasi7" target="_blank" rel="noreferrer" className="block text-gray-500 hover:text-pink-600 transition-colors duration-300">
              <Instagram size={28} strokeWidth={1.5} />
            </a>
          </li>
        </ul>
      </motion.div>
    </motion.div>
  );
};

export default AboutPage;
